// ─────────────────────────────────────────────────────────────────────
// DropshipReturnsPanel — mutual buyer/seller returns flow.
//
// Buyer side:
//   • File a return on a delivered/shipped order (photos + reason).
//   • Withdraw a filed return (before seller acts).
//   • Accept a seller's rejection.
//   • Push back on a seller's rejection (resets the 30d clock).
//
// Seller side:
//   • Verify a filed/contested return → queues a refund credit on the
//     next daily invoice.
//   • Reject a filed/contested return → must attach ≥ 1 proof photo.
//
// Both sides see the same row layout + resolution history + photo gallery
// for transparency.
// ─────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from "react";
import api, { money } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  RefreshCw, AlertTriangle, Check, X, Undo2, ArrowUpRight, Image as ImageIcon,
  PackageCheck, Clock, FileText,
} from "lucide-react";

const STATUS_LABELS = {
  filed:     { label: "Filed",     tone: "bg-amber-50 text-amber-800 border-amber-200" },
  rejected:  { label: "Rejected",  tone: "bg-rose-50 text-rose-700 border-rose-200" },
  contested: { label: "Contested", tone: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  verified:  { label: "Verified",  tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  applied:   { label: "Applied",   tone: "bg-foreground text-background border-transparent" },
  withdrawn: { label: "Withdrawn", tone: "bg-secondary text-foreground/50 border-border" },
  closed:    { label: "Closed",    tone: "bg-secondary text-foreground/50 border-border" },
};

const REASON_LABELS = {
  defective:               "Defective",
  wrong_item:              "Wrong item",
  not_as_described:        "Not as described",
  damaged_in_transit:      "Damaged in transit",
  customer_changed_mind:   "Customer changed mind",
  other:                   "Other",
};

const REJECTION_LABELS = {
  fake_item:             "Fake item",
  damaged_by_customer:   "Damaged by customer",
  outside_return_window: "Outside return window",
  not_our_sku:           "Not our SKU",
  not_received:          "Seller did not receive",
  other:                 "Other",
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(d)) return null;
  return Math.max(0, Math.ceil(d / 86400000));
};

// Convert a File to a base64 data URL (kept small at the UI edge).
const fileToDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(String(r.result || ""));
  r.onerror = rej;
  r.readAsDataURL(file);
});

export default function DropshipReturnsPanel({ role, orders = [], reload }) {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusChip, setStatusChip] = useState("all");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);   // selected return row (expanded inline)
  const [fileOpen, setFileOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(null);
  const [actionOpen, setActionOpen] = useState(null);  // { ret, action }
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/dropship/returns")
      .then(r => setReturns(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let xs = returns;
    if (statusChip !== "all") xs = xs.filter(r => r.status === statusChip);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      xs = xs.filter(r =>
        (r.order_code || "").toLowerCase().includes(q) ||
        (r.description || "").toLowerCase().includes(q) ||
        (r.reason || "").toLowerCase().includes(q)
      );
    }
    return xs;
  }, [returns, statusChip, search]);

  const count = (s) => returns.filter(r => r.status === s).length;

  // ─── Summary tiles ─────────────────────────────────────────────
  const summary = useMemo(() => {
    const open = returns.filter(r => ["filed", "contested"].includes(r.status));
    const verified = returns.filter(r => r.status === "verified");
    return {
      openCount: open.length,
      openTotal: open.reduce((a, r) => a + (Number(r.requested_refund) || 0), 0),
      verifiedCount: verified.length,
      verifiedTotal: verified.reduce((a, r) => a + (Number(r.refund_amount) || 0), 0),
    };
  }, [returns]);

  return (
    <div className="space-y-5" data-testid="dropship-returns-panel">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Open returns" value={summary.openCount} />
        <KpiTile label="Refund at stake" value={money(summary.openTotal)} />
        <KpiTile label="Verified · pending credit" value={summary.verifiedCount} tone="emerald" />
        <KpiTile label="Credit on next invoice" value={money(summary.verifiedTotal)} tone="emerald" />
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-1.5 flex-wrap">
          {[
            { k: "all",       l: "All",        n: returns.length, tone: "" },
            { k: "filed",     l: "Filed",      n: count("filed"),     tone: "bg-amber-50 text-amber-800 border-amber-200" },
            { k: "rejected",  l: "Rejected",   n: count("rejected"),  tone: "bg-rose-50 text-rose-700 border-rose-200" },
            { k: "contested", l: "Contested",  n: count("contested"), tone: "bg-indigo-50 text-indigo-700 border-indigo-200" },
            { k: "verified",  l: "Verified",   n: count("verified"),  tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
            { k: "applied",   l: "Applied",    n: count("applied"),   tone: "bg-foreground text-background border-transparent" },
            { k: "closed",    l: "Closed",     n: count("closed") + count("withdrawn"), tone: "bg-secondary text-foreground/50 border-border" },
          ].map(c => {
            const active = statusChip === c.k;
            return (
              <button key={c.k}
                onClick={() => setStatusChip(c.k)}
                data-testid={`returns-chip-${c.k}`}
                className={`text-[11.5px] mono uppercase tracking-[0.14em] px-3 py-1.5 rounded-full border transition-all ${active ? (c.tone || "bg-foreground text-background border-transparent") : "bg-white border-border text-foreground/70 hover:border-foreground/40"}`}>
                {c.l} {c.n > 0 && <span className="ml-1 opacity-70">({c.n})</span>}
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Input placeholder="Search by order / reason…" value={search} onChange={e => setSearch(e.target.value)} className="h-9 w-56" data-testid="returns-search"/>
          <Button variant="outline" onClick={load} disabled={loading} className="h-9 rounded-full gap-1.5 text-[12.5px]" data-testid="returns-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}/>Refresh
          </Button>
          {role === "buyer" && (
            <Button onClick={() => setFileOpen(true)} className="h-9 rounded-full gap-1.5 text-[12.5px] bg-foreground text-background hover:bg-foreground/90" data-testid="file-return-btn">
              <PackageCheck className="w-3.5 h-3.5"/>File return
            </Button>
          )}
        </div>
      </div>

      {/* List */}
      {loading && returns.length === 0 ? (
        <div className="rounded-2xl border border-border bg-white p-8 text-center text-[13px] text-foreground/50">Loading returns…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-border bg-white p-8 text-center text-[13px] text-foreground/50">
          {returns.length === 0
            ? (role === "buyer" ? "No returns filed yet. Click \"File return\" when a customer wants to send something back." : "No customer returns yet.")
            : "No returns match these filters."}
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-white overflow-hidden">
          {filtered.map((r, idx) => {
            const sl = STATUS_LABELS[r.status] || STATUS_LABELS.filed;
            const days = daysUntil(r.resolution_deadline);
            const isStale = days !== null && days <= 3 && ["filed", "contested", "rejected"].includes(r.status);
            return (
              <div key={r.id} className={`px-4 py-3 grid grid-cols-[1fr_auto] gap-3 items-center ${idx > 0 ? "border-t border-border" : ""} hover:bg-secondary/30 cursor-pointer`}
                   onClick={() => setDetail(detail?.id === r.id ? null : r)}
                   data-testid={`return-row-${r.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-medium">{r.order_code || r.order_id.slice(0, 8)}</span>
                    <span className={`text-[10.5px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full border ${sl.tone}`}>{sl.label}</span>
                    {r.auto_resolved && (
                      <span className="text-[10px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-foreground/5 border border-border text-foreground/60">Auto-resolved</span>
                    )}
                    {isStale && (
                      <span className="text-[10px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5"/>{days}d left
                      </span>
                    )}
                  </div>
                  <div className="text-[12px] text-foreground/65 mt-0.5 truncate">
                    {REASON_LABELS[r.reason] || r.reason} · {r.description}
                  </div>
                  <div className="text-[11px] text-foreground/50 mt-0.5">
                    Filed {new Date(r.filed_at).toLocaleDateString()}
                    {" · "}Qty {r.qty}
                    {r.rejection_reason && <> · Seller rejected: <em>{REJECTION_LABELS[r.rejection_reason] || r.rejection_reason}</em></>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="text-right">
                    <div className="text-[11px] mono uppercase tracking-[0.14em] text-foreground/50">Refund</div>
                    <div className="text-[14px] font-medium">{money(r.refund_amount ?? r.requested_refund)}</div>
                  </div>
                  {/* Action buttons per-role and per-status */}
                  <div className="flex gap-1">
                    {role === "seller" && (r.status === "filed" || r.status === "contested") && (
                      <>
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setVerifyOpen(r); }} className="h-8 rounded-full text-[11.5px] px-3 border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid={`verify-return-${r.id}`}>
                          <Check className="w-3 h-3 mr-1"/>Verify
                        </Button>
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setRejectOpen(r); }} className="h-8 rounded-full text-[11.5px] px-3 border-rose-200 text-rose-700 hover:bg-rose-50" data-testid={`reject-return-${r.id}`}>
                          <X className="w-3 h-3 mr-1"/>Reject
                        </Button>
                      </>
                    )}
                    {role === "buyer" && r.status === "filed" && (
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setActionOpen({ ret: r, action: "withdraw" }); }} className="h-8 rounded-full text-[11.5px] px-3" data-testid={`withdraw-return-${r.id}`}>
                        <Undo2 className="w-3 h-3 mr-1"/>Withdraw
                      </Button>
                    )}
                    {role === "buyer" && r.status === "rejected" && (
                      <>
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setActionOpen({ ret: r, action: "push_back" }); }} className="h-8 rounded-full text-[11.5px] px-3 border-amber-200 text-amber-800 hover:bg-amber-50" data-testid={`pushback-return-${r.id}`}>
                          <ArrowUpRight className="w-3 h-3 mr-1"/>Push back
                        </Button>
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setActionOpen({ ret: r, action: "accept_rejection" }); }} className="h-8 rounded-full text-[11.5px] px-3" data-testid={`accept-rejection-${r.id}`}>
                          <Check className="w-3 h-3 mr-1"/>Accept rejection
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Inline details when expanded */}
                {detail?.id === r.id && (
                  <ReturnDetail returnId={r.id} returnRow={r} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Dialogs */}
      {fileOpen && (
        <FileReturnDialog orders={orders} onClose={() => setFileOpen(false)} onSuccess={() => { setFileOpen(false); load(); reload && reload(); }}/>
      )}
      {verifyOpen && (
        <VerifyDialog ret={verifyOpen} onClose={() => setVerifyOpen(null)} onSuccess={() => { setVerifyOpen(null); load(); reload && reload(); }} submitting={submitting} setSubmitting={setSubmitting}/>
      )}
      {rejectOpen && (
        <RejectDialog ret={rejectOpen} onClose={() => setRejectOpen(null)} onSuccess={() => { setRejectOpen(null); load(); reload && reload(); }} submitting={submitting} setSubmitting={setSubmitting}/>
      )}
      {actionOpen && (
        <BuyerActionDialog ret={actionOpen.ret} action={actionOpen.action} onClose={() => setActionOpen(null)} onSuccess={() => { setActionOpen(null); load(); reload && reload(); }} submitting={submitting} setSubmitting={setSubmitting}/>
      )}
    </div>
  );
}

function KpiTile({ label, value, tone }) {
  const cls = tone === "emerald"
    ? "bg-emerald-50 border-emerald-200"
    : "bg-white border-border";
  return (
    <div className={`rounded-2xl border ${cls} p-4`} data-testid={`returns-kpi-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">{label}</div>
      <div className="display text-2xl mt-1 tracking-tight">{value}</div>
    </div>
  );
}

// ─── Inline detail block (fetches full payload w/ photos) ───────────────
function ReturnDetail({ returnId, returnRow }) {
  const [full, setFull] = useState(null);
  useEffect(() => {
    api.get(`/dropship/returns/${returnId}`)
      .then(r => setFull(r.data))
      .catch(() => {});
  }, [returnId]);
  const r = full || returnRow;
  return (
    <div className="col-span-full mt-2 -mx-4 px-4 py-3 border-t border-border bg-secondary/20 space-y-3" data-testid={`return-detail-${returnId}`}>
      {/* Customer photos */}
      {r.customer_photos?.length > 0 && (
        <div>
          <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 mb-2 flex items-center gap-1">
            <ImageIcon className="w-3 h-3"/>Customer photos ({r.customer_photos.length})
          </div>
          <div className="flex gap-2 flex-wrap">
            {r.customer_photos.map((p, i) => (
              <div key={i} className="rounded-lg border border-border overflow-hidden bg-white">
                {p.data_url
                  ? <img src={p.data_url} alt={p.caption || ""} className="w-24 h-24 object-cover" data-testid={`customer-photo-${i}`}/>
                  : <div className="w-24 h-24 grid place-items-center text-foreground/30"><ImageIcon className="w-6 h-6"/></div>}
                {p.caption && <div className="px-1.5 py-1 text-[10px] text-foreground/60 truncate max-w-[96px]">{p.caption}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rejection proof photos */}
      {r.proof_photos?.length > 0 && (
        <div>
          <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-rose-700 mb-2 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3"/>Seller rejection proof ({r.proof_photos.length})
          </div>
          <div className="flex gap-2 flex-wrap">
            {r.proof_photos.map((p, i) => (
              <div key={i} className="rounded-lg border border-rose-200 overflow-hidden bg-white">
                {p.data_url
                  ? <img src={p.data_url} alt={p.caption || ""} className="w-24 h-24 object-cover" data-testid={`proof-photo-${i}`}/>
                  : <div className="w-24 h-24 grid place-items-center text-foreground/30"><ImageIcon className="w-6 h-6"/></div>}
                {p.caption && <div className="px-1.5 py-1 text-[10px] text-foreground/60 truncate max-w-[96px]">{p.caption}</div>}
              </div>
            ))}
          </div>
          {r.rejection_note && <div className="text-[12px] text-foreground/70 mt-2 italic">"{r.rejection_note}"</div>}
        </div>
      )}

      {/* History */}
      {r.resolution_history?.length > 0 && (
        <div>
          <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 mb-2 flex items-center gap-1">
            <FileText className="w-3 h-3"/>Resolution history
          </div>
          <div className="space-y-1">
            {r.resolution_history.map((h, i) => (
              <div key={i} className="text-[11.5px] text-foreground/65 flex items-baseline gap-2">
                <span className="mono uppercase tracking-[0.14em] text-foreground/45 w-16 shrink-0">{h.actor_role}</span>
                <span className="font-medium">{h.action.replace(/_/g, ' ')}</span>
                <span className="ml-auto text-foreground/40 mono text-[10.5px]">{new Date(h.at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── File return dialog (buyer only) ──────────────────────────────────
function FileReturnDialog({ orders, onClose, onSuccess }) {
  const [orderId, setOrderId] = useState("");
  const [reason, setReason] = useState("defective");
  const [description, setDescription] = useState("");
  const [requestedRefund, setRequestedRefund] = useState("");
  const [photos, setPhotos] = useState([]);  // [{data_url, caption}]
  const [submitting, setSubmitting] = useState(false);

  const eligibleOrders = useMemo(() => {
    return (orders || []).filter(o => ["shipped", "delivered"].includes(o.status));
  }, [orders]);

  const pickedOrder = eligibleOrders.find(o => o.id === orderId);

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const accepted = [];
    for (const f of files.slice(0, 8 - photos.length)) {
      if (f.size > 4 * 1024 * 1024) {
        toast.error(`${f.name} is too large (4 MB max)`);
        continue;
      }
      const dataUrl = await fileToDataUrl(f);
      accepted.push({ data_url: dataUrl, caption: f.name });
    }
    setPhotos([...photos, ...accepted]);
  };

  const removePhoto = (i) => setPhotos(photos.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!orderId) { toast.error("Pick an order"); return; }
    if (!description.trim() || description.trim().length < 5) { toast.error("Describe the return in at least a sentence"); return; }
    setSubmitting(true);
    try {
      await api.post("/dropship/returns", {
        order_id: orderId,
        reason,
        description: description.trim(),
        requested_refund: requestedRefund === "" ? null : Number(requestedRefund),
        customer_photos: photos,
      });
      toast.success("Return filed — the seller will verify or reject it.");
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not file return");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="file-return-dialog">
        <DialogHeader>
          <DialogTitle>File a return</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-3">
          <div>
            <label className="text-[11.5px] mono uppercase tracking-[0.14em] text-foreground/55">Order</label>
            <select value={orderId} onChange={e => setOrderId(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-border px-3 text-[13px]" data-testid="return-order-select">
              <option value="">Select an order…</option>
              {eligibleOrders.map(o => (
                <option key={o.id} value={o.id}>
                  {o.code || o.id.slice(0, 8)} · {o.product_name || ""} · Qty {o.qty} · {money(Number(o.wholesale_cost) * Number(o.qty || 1))}
                </option>
              ))}
            </select>
            {eligibleOrders.length === 0 && <div className="text-[11.5px] text-foreground/50 mt-1 italic">No eligible orders (must be shipped or delivered).</div>}
          </div>

          <div>
            <label className="text-[11.5px] mono uppercase tracking-[0.14em] text-foreground/55">Reason</label>
            <select value={reason} onChange={e => setReason(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-border px-3 text-[13px]" data-testid="return-reason-select">
              {Object.entries(REASON_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>

          <div>
            <label className="text-[11.5px] mono uppercase tracking-[0.14em] text-foreground/55">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What did the customer say? What's wrong with the item?"
              rows={3}
              className="mt-1 w-full rounded-md border border-border bg-white p-3 text-[13px] resize-none focus:outline-none focus:ring-2 focus:ring-foreground/10"
              data-testid="return-description"
            />
          </div>

          <div>
            <label className="text-[11.5px] mono uppercase tracking-[0.14em] text-foreground/55">
              Requested refund <span className="text-foreground/40 normal-case">(defaults to order value)</span>
            </label>
            <Input type="number" min="0" step="0.01" value={requestedRefund} onChange={e => setRequestedRefund(e.target.value)}
                   placeholder={pickedOrder ? String(Number(pickedOrder.wholesale_cost) * Number(pickedOrder.qty || 1)) : "0.00"}
                   className="mt-1 h-10" data-testid="return-requested-refund"/>
          </div>

          <div>
            <label className="text-[11.5px] mono uppercase tracking-[0.14em] text-foreground/55">Customer photos (optional, up to 8)</label>
            <div className="flex gap-2 flex-wrap mt-1">
              {photos.map((p, i) => (
                <div key={i} className="relative">
                  <img src={p.data_url} alt="" className="w-20 h-20 object-cover rounded-lg border border-border"/>
                  <button type="button" onClick={() => removePhoto(i)} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-rose-600 text-white grid place-items-center text-[10px]">×</button>
                </div>
              ))}
              {photos.length < 8 && (
                <label className="w-20 h-20 rounded-lg border border-dashed border-border grid place-items-center cursor-pointer hover:bg-secondary/30">
                  <input type="file" multiple accept="image/*" onChange={onPickPhotos} className="hidden" data-testid="return-photo-input"/>
                  <span className="text-[11px] text-foreground/55 text-center">+ Add<br/>photo</span>
                </label>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-border mt-4">
          <Button variant="outline" onClick={onClose} className="rounded-full h-9 px-4" data-testid="return-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting || !orderId || !description.trim()} className="rounded-full h-9 px-4 bg-foreground text-background hover:bg-foreground/90" data-testid="return-submit">
            {submitting ? "Filing…" : "File return"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Verify (seller) ─────────────────────────────────────────────────
function VerifyDialog({ ret, onClose, onSuccess, submitting, setSubmitting }) {
  const [finalRefund, setFinalRefund] = useState(String(ret.requested_refund || ""));
  const [note, setNote] = useState("");
  const submit = async () => {
    setSubmitting(true);
    try {
      await api.patch(`/dropship/returns/${ret.id}/verify`, {
        final_refund: finalRefund === "" ? null : Number(finalRefund),
        note: note.trim() || null,
      });
      toast.success("Return verified — credit queued for the next invoice.");
      onSuccess();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="verify-return-dialog">
        <DialogHeader><DialogTitle>Verify return on {ret.order_code}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-3">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-[12.5px] text-emerald-900">
            <strong>You received and inspected the goods.</strong> Confirming verifies the refund will appear as a credit on the next daily invoice (prior-period adjustment).
          </div>
          <div>
            <label className="text-[11.5px] mono uppercase tracking-[0.14em] text-foreground/55">Final refund amount</label>
            <Input type="number" min="0" step="0.01" value={finalRefund} onChange={e => setFinalRefund(e.target.value)} className="mt-1 h-10" data-testid="verify-final-refund"/>
            <div className="text-[11px] text-foreground/50 mt-1">Buyer requested: {money(ret.requested_refund)} · Order value: {money(ret.order_value)}</div>
          </div>
          <div>
            <label className="text-[11.5px] mono uppercase tracking-[0.14em] text-foreground/55">Note (optional)</label>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                      className="mt-1 w-full rounded-md border border-border bg-white p-2 text-[13px] resize-none"
                      data-testid="verify-note"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-border mt-4">
          <Button variant="outline" onClick={onClose} className="rounded-full h-9 px-4">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="rounded-full h-9 px-4 bg-emerald-600 text-white hover:bg-emerald-700" data-testid="verify-submit">
            {submitting ? "Verifying…" : "Verify return"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Reject (seller) ─────────────────────────────────────────────────
function RejectDialog({ ret, onClose, onSuccess, submitting, setSubmitting }) {
  const [reason, setReason] = useState("fake_item");
  const [note, setNote] = useState("");
  const [photos, setPhotos] = useState([]);

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const accepted = [];
    for (const f of files.slice(0, 8 - photos.length)) {
      if (f.size > 4 * 1024 * 1024) {
        toast.error(`${f.name} is too large (4 MB max)`);
        continue;
      }
      const dataUrl = await fileToDataUrl(f);
      accepted.push({ data_url: dataUrl, caption: f.name });
    }
    setPhotos([...photos, ...accepted]);
  };

  const submit = async () => {
    if (photos.length === 0) { toast.error("At least one proof photo is required"); return; }
    if (!note.trim() || note.trim().length < 5) { toast.error("Explain the rejection in at least a sentence"); return; }
    setSubmitting(true);
    try {
      await api.patch(`/dropship/returns/${ret.id}/reject`, {
        reason, note: note.trim(), proof_photos: photos,
      });
      toast.success("Return rejected — buyer has 30 days to push back.");
      onSuccess();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="reject-return-dialog">
        <DialogHeader><DialogTitle>Reject return on {ret.order_code}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-3">
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-[12.5px] text-rose-900">
            Rejections require <strong>proof photos</strong>. The buyer can accept your rejection or push back — if they push back, you get 30 days to respond before it auto-verifies in their favor.
          </div>
          <div>
            <label className="text-[11.5px] mono uppercase tracking-[0.14em] text-foreground/55">Reason</label>
            <select value={reason} onChange={e => setReason(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-border px-3 text-[13px]" data-testid="reject-reason-select">
              {Object.entries(REJECTION_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11.5px] mono uppercase tracking-[0.14em] text-foreground/55">Explanation</label>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
                      placeholder="What's wrong with the return? What do the photos show?"
                      className="mt-1 w-full rounded-md border border-border bg-white p-3 text-[13px] resize-none"
                      data-testid="reject-note"/>
          </div>
          <div>
            <label className="text-[11.5px] mono uppercase tracking-[0.14em] text-rose-700">Proof photos · required</label>
            <div className="flex gap-2 flex-wrap mt-1">
              {photos.map((p, i) => (
                <div key={i} className="relative">
                  <img src={p.data_url} alt="" className="w-20 h-20 object-cover rounded-lg border border-rose-200"/>
                  <button type="button" onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-rose-600 text-white grid place-items-center text-[10px]">×</button>
                </div>
              ))}
              {photos.length < 8 && (
                <label className="w-20 h-20 rounded-lg border border-dashed border-rose-300 grid place-items-center cursor-pointer hover:bg-rose-50">
                  <input type="file" multiple accept="image/*" onChange={onPickPhotos} className="hidden" data-testid="reject-photo-input"/>
                  <span className="text-[11px] text-rose-700 text-center">+ Add<br/>proof</span>
                </label>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-border mt-4">
          <Button variant="outline" onClick={onClose} className="rounded-full h-9 px-4">Cancel</Button>
          <Button onClick={submit} disabled={submitting || photos.length === 0} className="rounded-full h-9 px-4 bg-rose-600 text-white hover:bg-rose-700" data-testid="reject-submit">
            {submitting ? "Rejecting…" : "Reject return"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Buyer actions (withdraw / accept_rejection / push_back) ─────────
function BuyerActionDialog({ ret, action, onClose, onSuccess, submitting, setSubmitting }) {
  const [note, setNote] = useState("");
  const copy = {
    withdraw:         { title: "Withdraw return", tone: "bg-secondary border-border text-foreground/80", body: "Your return request will be withdrawn. No refund will apply." },
    accept_rejection: { title: "Accept the seller's rejection", tone: "bg-secondary border-border text-foreground/80", body: "You agree with the seller — the return is closed and no refund will apply." },
    push_back:        { title: "Push back on the rejection", tone: "bg-amber-50 border-amber-200 text-amber-900", body: "You're insisting the return is valid. The seller has 30 days to respond — if they stay silent, the return will auto-verify in your favor and the refund will appear on the next invoice." },
  }[action];
  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/dropship/returns/${ret.id}/buyer-action`, { action, note: note.trim() || null });
      toast.success(copy.title);
      onSuccess();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="buyer-action-dialog">
        <DialogHeader><DialogTitle>{copy.title}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-3">
          <div className={`rounded-xl border p-3 text-[12.5px] ${copy.tone}`}>{copy.body}</div>
          <div>
            <label className="text-[11.5px] mono uppercase tracking-[0.14em] text-foreground/55">Note (optional)</label>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                      className="mt-1 w-full rounded-md border border-border bg-white p-2 text-[13px] resize-none"
                      data-testid="buyer-action-note"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-border mt-4">
          <Button variant="outline" onClick={onClose} className="rounded-full h-9 px-4">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="rounded-full h-9 px-4 bg-foreground text-background hover:bg-foreground/90" data-testid="buyer-action-submit">
            {submitting ? "Submitting…" : "Confirm"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
